import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { AttendanceApi, AttendanceStatus, StudentAttendanceDailyRow } from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { useDefaultYear } from './useDefaultYear';
import { formatDate } from '@/lib/format';

const STATUS_TONE: Record<AttendanceStatus, 'green' | 'red' | 'amber' | 'blue' | 'slate'> = {
  present: 'green',
  absent: 'red',
  late: 'amber',
  half_day: 'blue',
  holiday: 'slate',
};

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  half_day: 'Half Day',
  holiday: 'Holiday',
};

const columnHelper = createColumnHelper<StudentAttendanceDailyRow>();

const COLUMNS = [
  columnHelper.accessor('date', { header: 'Date', cell: (ctx) => formatDate(ctx.getValue()) }),
  columnHelper.accessor('admissionNo', { header: 'Admission No' }),
  columnHelper.accessor('studentName', { header: 'Name' }),
  columnHelper.accessor('className', { header: 'Class' }),
  columnHelper.accessor('sectionName', { header: 'Section' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (ctx) => <Badge tone={STATUS_TONE[ctx.getValue()]}>{STATUS_LABEL[ctx.getValue()]}</Badge>,
  }),
];

/** Flat, filterable (student, date, status) listing across a range — every real attendance event, not the marking grid. Reuses StudentAttendanceService.dailyList(). */
export function StudentAttendanceReportPage() {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'date', defaultSortOrder: 'desc' });
  const { years, yearId, setYearId, effectiveYearId, selectedYear, isLoading: yearLoading, hasAnyData } =
    useDefaultYear();

  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState<AttendanceStatus | ''>('');

  useEffect(() => {
    if (selectedYear) {
      setStartDate(selectedYear.startDate.slice(0, 10));
      setEndDate(selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'students-daily', dt.params, effectiveYearId, classId, sectionId, startDate, endDate, type],
    queryFn: () =>
      AttendanceApi.studentsDaily({
        ...dt.params,
        academicYearId: effectiveYearId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        type: type || undefined,
      }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Student Attendance Report"
        description={`Every recorded ${term.group.toLowerCase()} attendance event over a date range.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <Select className="!w-36" value={type} onChange={(e) => setType(e.target.value as AttendanceStatus | '')}>
          <option value="">All types</option>
          {(Object.keys(STATUS_LABEL) as AttendanceStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
      ) : (
        <DataTable
          columns={COLUMNS}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.attendanceId}
        />
      )}
    </>
  );
}
